/**
 * Erros de validação da API, por campo (CRM-40).
 *
 * O `buildError` do AuthContext guarda o corpo da resposta em `err.payload`
 * e junta tudo numa frase só em `err.message` ("contato_email: ... |
 * documento: ..."). Isso serve para o toast, mas não para marcar o campo:
 * as telas liam `err.data` (que não existe) ou só `titulo`, e a mensagem
 * nunca aparecia junto do campo com erro.
 *
 * Formato que o backend devolve (`core/exceptions.py`):
 *   400 de serializer   -> { erros: { campo: "msg" | { sub: "msg" } } }
 *   regra de domínio    -> { detalhe: "msg" }  (TransicaoInvalida)
 *   403/404/500         -> { erro: "msg" }
 *
 * Uso: `setErros(errosPorCampo(err))`; mensagem geral em `erros[CAMPO_GERAL]`.
 */

/** Chave da mensagem que não pertence a um campo (vai no topo do formulário). */
export const CAMPO_GERAL = "__nonfield__";

/** Chaves de `erros` que NÃO são campo de formulário — não viram rótulo. */
const CHAVES_SEM_ROTULO = ["detalhe", "detail"];

function juntarValor(valor) {
  if (Array.isArray(valor)) return valor.map(juntarValor).join(", ");
  if (valor && typeof valor === "object") {
    return Object.entries(valor)
      .map(([campo, msg]) => `${campo}: ${juntarValor(msg)}`)
      .join(" | ");
  }
  return String(valor);
}

/**
 * Mensagem única de uma resposta de erro da API — o que vai no toast e em
 * `err.message`.
 *
 * Ordem: `erro` -> `detalhe` -> `detail` -> `erros` (por campo) -> `mensagem`
 * -> status. Em `erros`, uma chave que não é campo (`detalhe`/`detail`) só
 * ganha rótulo quando vem acompanhada de campos de verdade; sozinha, ela É a
 * mensagem (`unidade_do_request` manda `{erros: {detalhe: "..."}}`).
 */
export function mensagemDeErro(payload, status) {
  const erros = ehObjeto(payload?.erros) ? payload.erros : null;
  const chaves = erros ? Object.keys(erros) : [];
  const soSemRotulo =
    chaves.length === 1 && CHAVES_SEM_ROTULO.includes(chaves[0]);
  const daValidacao = erros
    ? soSemRotulo
      ? juntarValor(erros[chaves[0]])
      : chaves.map((campo) => `${campo}: ${juntarValor(erros[campo])}`).join(" | ")
    : null;

  return (
    payload?.erro ||
    payload?.detalhe ||
    payload?.detail ||
    daValidacao ||
    payload?.mensagem ||
    `Falha na API (${status})`
  );
}

// Onde o DRF põe o erro do `validate()` do serializer, que não é de um campo.
const CHAVE_NAO_CAMPO_DRF = "non_field_errors";

// Mesma ordem de preferência do `buildError`.
const CHAVES_MENSAGEM_GERAL = ["erro", "detalhe", "detail", "mensagem"];

function ehObjeto(valor) {
  return Boolean(valor) && typeof valor === "object" && !Array.isArray(valor);
}

function semConteudo(valor) {
  if (valor === null || valor === undefined || valor === "") return true;
  if (Array.isArray(valor)) return valor.length === 0;
  return ehObjeto(valor) && Object.keys(valor).length === 0;
}

function caminhoDe(caminho, chave) {
  if (chave === CHAVE_NAO_CAMPO_DRF) return caminho;
  return caminho ? `${caminho}.${chave}` : chave;
}

/**
 * Achata o objeto de erros em `{ "campo.sub": "mensagem" }`.
 *
 * Lista de itens de sub-serializer (`many=True`) traz `{}` para cada item
 * válido: pula os vazios e usa o primeiro com erro, como o backend faz.
 */
function achatar(valor, caminho, destino) {
  if (semConteudo(valor)) return;
  if (Array.isArray(valor)) {
    const itemComErro = valor.find((item) => ehObjeto(item) && !semConteudo(item));
    if (itemComErro) {
      achatar(itemComErro, caminho, destino);
      return;
    }
    const textos = valor.filter((item) => !semConteudo(item) && !ehObjeto(item));
    if (textos.length) destino[caminho || CAMPO_GERAL] = textos.map(String).join(" ");
    return;
  }
  if (ehObjeto(valor)) {
    Object.entries(valor).forEach(([chave, item]) => {
      achatar(item, caminhoDe(caminho, chave), destino);
    });
    return;
  }
  destino[caminho || CAMPO_GERAL] = String(valor);
}

/**
 * Erro lançado pelo `request` do AuthContext -> `{ campo: mensagem }`, com a
 * mensagem geral (detalhe de domínio, erro de rede) em `CAMPO_GERAL`.
 */
export function errosPorCampo(err) {
  if (!err) return {};
  const payload = ehObjeto(err.payload) ? err.payload : {};
  const erros = {};
  achatar(payload.erros, "", erros);

  const geral = CHAVES_MENSAGEM_GERAL
    .map((chave) => payload[chave])
    .find((mensagem) => typeof mensagem === "string" && mensagem.trim());
  if (geral && !erros[CAMPO_GERAL]) erros[CAMPO_GERAL] = geral;

  // Sem nada aproveitável (rede, timeout, payload estranho): a mensagem do
  // próprio erro, para o formulário nunca falhar calado.
  if (Object.keys(erros).length === 0 && err.message) {
    erros[CAMPO_GERAL] = err.message;
  }
  return erros;
}
